import React, {useEffect, useState, useCallback, useContext} from 'react';
import {setOptions, toast} from "@mobiscroll/react";
import '@mobiscroll/react/dist/css/mobiscroll.min.css';
import './styles/dashboard.css';
import {Chart as ChartJS, CategoryScale, LinearScale, ArcElement, BarElement,  PointElement, LineElement, Title, Tooltip, Legend, Filler, PolarAreaController, RadialLinearScale} from 'chart.js';
import Empty from './utils/Empty';
import Queue from "./pages/Queue";
import Calendar from "./pages/Calendar";
import Patient from "./pages/Patient";
import DashboardHeader from "./blocks/DashboardHeader";
import QR from "./shared/QR";
import {NetworkContext} from "./utils/NetworkContext";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler, PolarAreaController, RadialLinearScale);

setOptions({
    theme: 'ios',
    themeVariant: 'light',
});

function Dashboard({locale, Navigation, navigation, dialog, Dialog, width, configuration, token, wss, setLocale}) {
    const {wsCall, wsResponse, setWsResponse} = useContext(NetworkContext);
    const [user, setUser] = useState({});
    const [patient, setPatient] = useState({});
    const [visit, setVisit] = useState({});
    const [location, setLocation] = useState('');
    const [qr, setQr] = useState({});
    const [refresh, setRefresh] = useState(false);

    const Start = () => {
        const message = {
            type: 'wss',
            path: 'user-login',
            action: 'get',
            data: {}
        };
        wsCall(message);
    }

    const GetUserLogin = useCallback(data => {
        if (data.user) {
            setUser(data.user);
        }
        if (data.user && data.user.location && configuration.locations.some(item => item.value === data.user.location)) {

            setLocation(data.user.location);
        } else if (data.user && !data.user.location && configuration.locations.length !== 0) {

            setLocation(configuration.locations[0].value);
        }
    }, [configuration.locations]);

    const UpdateUser = useCallback(data => {
        if (data.user) {
            setUser(data.user);
        }
        if (data.toast) {
            toast({message: locale.dashboard.a, color: 'info', display: 'bottom', duration: 3000});
        }
        if (refresh) {
            setRefresh(false);
        }
        if (dialog.view) {
            Dialog({view: 'close'});
        }
    }, [locale.dashboard.a, refresh, dialog, Dialog]);

    const GetPatient = useCallback(data => {
        if (data.patient) {
            setPatient(data.patient);
        }
        if (data.source && data.source === 'dashboardHeaderPatients') {

            Dialog({view: 'close'});
            Navigation({page: 'patient', tab: 'visits'});
        } else if (data.source && data.source === 'sharedEvent') {

            setVisit({});
            Dialog({view: 'close'});
            Navigation({page: 'patient', tab: 'visits', save: true});
        } else if (data.source) {

            Navigation({page: 'patient', tab: 'visit', save: true});
        }
    }, [Navigation, Dialog]);

    const PutShortenerMicro = useCallback(data => {
        if (data.qr) {
            setQr(data.qr);
        } else {
            setQr({});
        }
        if (data.qr && data.dialog) {
            Dialog({view: 'sharedQr'});
        }
    }, [Dialog]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'get' && wsResponse.path === 'patient') {
            GetPatient(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, GetPatient, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'update' && wsResponse.path === 'user') {
            UpdateUser(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, UpdateUser, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'get' && wsResponse.path === 'user-login') {
            GetUserLogin(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, GetUserLogin, setWsResponse]);

    useEffect(() => {
        if (wsResponse && !Empty(wsResponse) && wsResponse.action === 'put' && wsResponse.path === 'shortener-micro') {
            PutShortenerMicro(wsResponse.data);
            setWsResponse({});
        }
    }, [wsResponse, PutShortenerMicro, setWsResponse]);

    useEffect(() => {
        Start();
    }, []);

    return (
        <>
            {!Empty(user) &&
                <DashboardHeader
                    Navigation={Navigation}
                    locale={locale}
                    navigation={navigation}
                    user={user}
                    setUser={setUser}
                    dialog={dialog}
                    Dialog={Dialog}
                    width={width}
                    configuration={configuration}
                    token={token}
                    wss={wss}
                    setLocale={setLocale}
                    setVisit={setVisit}
                    setPatient={setPatient}
                    patient={patient}
                    location={location}
                    setLocation={setLocation}
                    setRefresh={setRefresh}
                />
            }
            {navigation.page === 'queue' &&
                <Queue
                    locale={locale}
                    width={width}
                    navigation={navigation}
                    Navigation={Navigation}
                    configuration={configuration}
                    dialog={dialog}
                    Dialog={Dialog}
                    user={user}
                    setPatient={setPatient}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    setVisit={setVisit}
                    visit={visit}
                    location={location}
                    qr={qr}
                    setQr={setQr}
                />
            }
            {navigation.page === 'calendar' &&
                <Calendar
                    locale={locale}
                    width={width}
                    navigation={navigation}
                    configuration={configuration}
                    dialog={dialog}
                    Dialog={Dialog}
                    user={user}
                    setRefresh={setRefresh}
                    refresh={refresh}
                    location={location}
                />
            }
            {navigation.page === 'patient' && !Empty(patient) &&
                <Patient
                    locale={locale}
                    width={width}
                    navigation={navigation}
                    Navigation={Navigation}
                    configuration={configuration}
                    dialog={dialog}
                    Dialog={Dialog}
                    patient={patient}
                    setPatient={setPatient}
                    visit={visit}
                    setVisit={setVisit}
                    location={location}
                    setRefresh={setRefresh}
                    refresh={refresh}
                />
            }
            {dialog.view === 'sharedQr' && !Empty(qr) &&
                <QR
                    setQr={setQr}
                    dialog={dialog}
                    Dialog={Dialog}
                    qr={qr}
                    width={width}
                    locale={locale}
                />
            }
        </>
    );
}

export default Dashboard;
